import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronRight,
  AlertTriangle,
  Download,
  Printer,
  X,
  Loader2,
  RefreshCw,
  Monitor,
} from "lucide-react";
import {
  downloadPrintHelperInstaller,
  listHelperPrinters,
  pingPrintHelper,
  printViaHelper,
  isHelperVersionOk,
  PRINT_HELPER_VERSION,
  PRINT_HELPER_FILENAME,
  type HelperPrinter,
} from "@/services/printHelper";

type StepId = "helper" | "install" | "pick" | "test" | "done";

interface Props {
  open: boolean;
  onClose: () => void;
  windowsPrinterName: string | null;
  onSelectPrinter: (name: string) => void;
  onSaveSettings: () => void;
  headerText: string;
  feedLines: number;
  autoCut: boolean;
}

const STEPS: { id: StepId; label: string }[] = [
  { id: "helper", label: "Helper" },
  { id: "install", label: "Install" },
  { id: "pick", label: "Printer" },
  { id: "test", label: "Test" },
  { id: "done", label: "Done" },
];

function buildTestBytes(header: string, feedLines: number, autoCut: boolean): Uint8Array {
  const lines = [
    header.trim() || "TEST PRINT",
    "------------------------------",
    "SALE DESK PRINTER TEST",
    `Time: ${new Date().toLocaleString()}`,
    "",
    "If you can read this, the helper",
    "and USB printer are working.",
    "",
  ].join("\n") + "\n".repeat(Math.max(1, feedLines));

  const enc = new TextEncoder().encode(lines);
  if (!autoCut) return enc;
  const cut = new Uint8Array([0x1d, 0x56, 0x42, 0x00]);
  const out = new Uint8Array(enc.length + cut.length);
  out.set(enc, 0);
  out.set(cut, enc.length);
  return out;
}

export default function UsbPrinterSetupWizard({
  open,
  onClose,
  windowsPrinterName,
  onSelectPrinter,
  onSaveSettings,
  headerText,
  feedLines,
  autoCut,
}: Props) {
  const [step, setStep] = useState<StepId>("helper");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helperOk, setHelperOk] = useState<boolean | null>(null);
  const [printers, setPrinters] = useState<HelperPrinter[]>([]);
  const [selected, setSelected] = useState<string>(windowsPrinterName || "");

  const stepIndex = useMemo(() => STEPS.findIndex((s) => s.id === step), [step]);

  const checkHelper = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const ping = await pingPrintHelper();
    if (!ping) {
      setHelperOk(false);
      setBusy(false);
      return false;
    }
    if (!isHelperVersionOk(ping.version)) {
      setHelperOk(false);
      setBusy(false);
      setError(
        `Old Print Helper detected (v${ping.version || "?"} ). Close old helpers in Task Manager, download ${PRINT_HELPER_FILENAME}, and run it once. Required: v${PRINT_HELPER_VERSION}+ (silent background).`
      );
      return false;
    }
    setHelperOk(true);
    setBusy(false);
    return true;
  };

  const loadPrinters = async () => {
    setBusy(true);
    setError(null);
    try {
      const list = await listHelperPrinters();
      setPrinters(list);
      if (!selected && list.length === 1) setSelected(list[0].name);
      if (!list.length) {
        setError("No Windows printers found. Install the printer in Windows first, then refresh.");
      }
    } catch (e: any) {
      setError(e?.message || "Could not load printers. Is the helper running?");
      setPrinters([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setStep("helper");
    setError(null);
    setHelperOk(null);
    setPrinters([]);
    setSelected(windowsPrinterName || "");
    void (async () => {
      const ok = await checkHelper();
      if (ok) {
        setStep("pick");
        await loadPrinters();
      } else {
        setStep("install");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const finishSelect = () => {
    if (!selected) {
      setError("Select a printer from the list.");
      return;
    }
    onSelectPrinter(selected);
    setError(null);
    setStep("test");
  };

  const runTest = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      onSelectPrinter(selected);
      onSaveSettings();
      const bytes = buildTestBytes(headerText, feedLines, autoCut);
      await printViaHelper(selected, bytes);
      setStep("done");
    } catch (e: any) {
      setError(e?.message || "Test print failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
        role="dialog"
        aria-labelledby="usb-wizard-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-gray-800" />
            <h2 id="usb-wizard-title" className="text-base font-semibold text-gray-900">
              Add USB printer
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pt-4 flex gap-1">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`h-1 flex-1 rounded-full ${i <= stepIndex ? "bg-gray-900" : "bg-gray-200"}`}
            />
          ))}
        </div>
        <p className="px-5 pt-2 text-xs text-gray-400">
          Step {stepIndex + 1} of {STEPS.length}: {STEPS[stepIndex].label}
        </p>

        <div className="px-5 py-4 min-h-[240px]">
          {error && (
            <div className="mb-3 flex gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === "helper" && (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm">Checking Sale Desk Print Helper…</p>
            </div>
          )}

          {step === "install" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Install <strong>Sale Desk Print Helper v{PRINT_HELPER_VERSION}</strong> once on this
                Windows PC. It runs silently in the background (no command window) and starts with
                Windows.
              </p>
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1.5">
                <li>
                  Close any old helper: Task Manager → end old{" "}
                  <strong>SaleDesk-Print-Helper</strong> / black console windows.
                </li>
                <li>
                  Click Download (
                  <code className="font-mono text-xs">{PRINT_HELPER_FILENAME}</code>).
                </li>
                <li>Run the file once (SmartScreen → More info → Run anyway if asked).</li>
                <li>You should see a short “Installed successfully” message — then it stays in the background.</li>
                <li>Click <strong>I installed it — check again</strong>.</li>
              </ol>
              <button
                type="button"
                onClick={() => downloadPrintHelperInstaller()}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-700"
              >
                <Download className="w-4 h-4" />
                Download Print Helper v{PRINT_HELPER_VERSION}
              </button>
              <p className="text-xs text-gray-400 text-center">
                File: <code className="font-mono">{PRINT_HELPER_FILENAME}</code>
              </p>
              <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                <Monitor className="w-3.5 h-3.5 shrink-0" />
                One-time install. No CMD window. Auto-starts at Windows sign-in.
              </div>
            </div>
          )}

          {step === "pick" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">Select the Windows printer used for receipts:</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void loadPrinters()}
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
              {helperOk && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-1.5">
                  Print Helper is running
                </p>
              )}
              <div className="max-h-48 overflow-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                {printers.length === 0 && !busy && (
                  <p className="text-sm text-gray-400 text-center py-6">No printers listed yet</p>
                )}
                {printers.map((p) => {
                  const active = selected === p.name;
                  return (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => setSelected(p.name)}
                      className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between ${
                        active ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-800"
                      }`}
                    >
                      <span className="font-medium truncate">{p.name}</span>
                      {active && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === "test" && (
            <div className="space-y-3">
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">Selected printer</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{selected}</p>
              </div>
              <p className="text-sm text-gray-600">
                Send a short test page to confirm paper feed and connection.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => void runTest()}
                className="w-full flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                {busy ? "Printing…" : "Send test print"}
              </button>
              <button
                type="button"
                onClick={() => {
                  onSelectPrinter(selected);
                  onSaveSettings();
                  setStep("done");
                }}
                className="w-full text-sm text-gray-500 hover:text-gray-800 py-2"
              >
                Skip test and finish
              </button>
            </div>
          )}

          {step === "done" && (
            <div className="space-y-3 text-center py-6">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <p className="text-base font-medium text-gray-900">USB printer ready</p>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-800">{selected}</span> is saved for this
                browser. Keep Sale Desk Print Helper running on this PC.
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-2">
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800 px-2 py-2">
            {step === "done" ? "Close" : "Cancel"}
          </button>
          <div className="flex gap-2">
            {step === "install" && (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  const ok = await checkHelper();
                  if (ok) {
                    setStep("pick");
                    await loadPrinters();
                  } else {
                    setError("Helper not detected yet. Finish install, start it, then try again.");
                  }
                }}
                className="inline-flex items-center gap-1 text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                I installed it — check again
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === "pick" && (
              <button
                type="button"
                disabled={!selected || busy}
                onClick={finishSelect}
                className="inline-flex items-center gap-1 text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {step === "done" && (
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
